<script setup lang="ts">
import { ref, watch, toRefs, computed, onMounted } from 'vue';
const emit = defineEmits(['update:modelValue', 'change']);

interface List {
  id: number;
  label: string;
}

// 定义 props 类型
interface Props {
  modelValue: number; //当前值
  list: List[]; // 列表
  disabled?: boolean; // 是否禁用
  disabledOther?: boolean; // 是否禁用其它选项
  showId?: number; // 显示的id
  icon?: string; // 图标
}

// 使用 withDefaults 设置默认值
const props = defineProps({
  modelValue: Number,
  list: {
    type: Array as () => List[], // 类型断言
    default: () => [],
  },
  disabled: Boolean, // 是否禁用
  disabledOther: {
    type: Boolean,
    default: false,
  }, // 是否禁用其它选项
  showId: {
    type: Number,
    default: 0,
  },
  icon: {
    type: String,
    default: 'icon_host',
  },
});

const { list, disabled, disabledOther, showId, icon } = toRefs(props);
const currentTab = ref(0);

const currentValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const selectTab = (id: number, index: number) => {
  if (props.disabled) return;
  if (props.disabledOther && id !== showId.value) return;
  currentValue.value = id;
  currentTab.value = index;
  emit('change', id);
};

watch(
  () => currentValue.value,
  (newValue) => {
    currentTab.value = list.value.findIndex(
      (item) => item.id === props.modelValue
    );
  },
  { immediate: true }
);

onMounted(() => {});
</script>

<template>
  <div class="tabs-wrap">
    <div class="tabs-box fr-ac" :class="{ opa4: disabled }">
      <div
        v-for="(item, index) in list"
        :key="item.id"
        class="tabs-item"
        :class="{
          'item-active': currentValue === item.id,
          'opa4 cursor-not-allowed': disabledOther && item.id !== showId,
        }"
        @click="selectTab(item.id, index)"
      >
        <div class="fr-ac-jc w-per-100" style="display: flex; flex-wrap: nowrap; align-items: center; justify-content: center;">
          <IconFont
            v-if="currentValue === item.id"
            :name="icon"
            class="mr-2"
            :size="14"
            style="flex-shrink: 0;"
          />
          <div class="fs-12" style="flex-shrink: 0;">{{ item.label }}</div>
        </div>
      </div>
      <div
        v-if="currentTab !== -1"
        class="translocation-box"
        :style="{ left: `calc(2px + ${currentTab} * var(--tab-item-width, 88px))` }"
      ></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tabs-wrap {
  padding: 3px;
  background: var(--tab-bg-color);
  border-radius: var(--border-radius);
  cursor: pointer;
}

.tabs-box {
  height: 26px;
  border-radius: var(--border-radius);
  position: relative;
  z-index: 1;
  padding: 2px; /* 统一加点内边距，让背景块像胶囊一样 */

  .tabs-item {
    width: var(--tab-item-width, 88px);
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-color-light);
    border-radius: calc(var(--border-radius) - 2px);
    position: relative;
    z-index: 1;
    cursor: pointer;
  }

  .tabs-item .fs-12 {
    white-space: nowrap !important;
    line-height: 1;
  }

  .item-active {
    color: var(--text-color-white);
  }

  .cursor-not-allowed {
    cursor: not-allowed;
  }

  .translocation-box {
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    background: var(--tab-bg-color-active);
    position: absolute;
    top: 2px;
    width: var(--tab-item-width, 88px);
    height: calc(100% - 4px);
    z-index: 0;
    border-radius: calc(var(--border-radius) - 2px);
  }
}

@media (max-width: 767px) {
  .tabs-box {
    width: auto !important;
    display: inline-flex !important;
    flex-wrap: nowrap !important;
    background: var(--tab-bg-color);
    border-radius: var(--border-radius);
    padding: 2px;
    height: 32px;
    --tab-item-width: 104px;
    
    .tabs-item {
      flex: none !important;
      width: var(--tab-item-width) !important;
      padding: 0;
      display: inline-flex !important;
      flex-direction: row !important;
      align-items: center !important;
      justify-content: center !important;
      flex-wrap: nowrap !important;
      
      &.item-active {
        background: transparent;
      }
    }

    .translocation-box {
      width: var(--tab-item-width) !important;
      height: 28px !important;
      display: block !important;
    }
  }
}
</style>
